import { useState, useMemo, useRef } from 'react';
import { useForm, FormProvider } from '@xmanscript/useform';
import { PlusIcon } from '@radix-ui/react-icons';
// import Container from '@/ui/_lib/Container';
import Button from '@/ui/_lib/Button';
import AddLinkDrawer from '@/ui/_lib/Drawer/AddLinkDrawer';
import DeleteLinkDrawer from '@/ui/_lib/Drawer/DeleteLinkDrawer';
import Link from '@/ui/_lib/links/LinksTab/Link';
import LinkLoader from '@/ui/_lib/loader/LinkLoader';
import SuccessMessage from '@/ui/_lib/Drawer/SuccessMessage';
import { AuthenticatedLinkService, ProductService, UserService } from '@/ui/_apiUrls';
import Product from '@/ui/_lib/links/TemplateTab/Product';
import FormControl from '@/ui/_lib/FormControl';
import AddProductDrawer from '@/ui/_lib/Drawer/AddProductDrawer';
import DeleteProductDrawer from '@/ui/_lib/Drawer/DeleteProductDrawer';
import { ReactComponent as ImageIcon } from '@/assets/icons/image.svg';
import { ReactComponent as PlusIconSvg } from '@/assets/icons/plus.svg';
import { useUser } from '@/contexts/UserContext/UserContext';

function Template1() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editableLink, setEditableLink] = useState(null);
  const [isSuccessMessageOpen, setIsSuccessMessageOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDeleteDrawerOpen, setIsDeleteDrawerOpen] = useState(false);
  const [selectedLinkForDelete, setSelectedLinkForDelete] = useState<Record<string, any> | null>(null);

  // Add ref to track if we've already shown the limit error
  const hasShownLinkLimitError = useRef(false);
  const hasShownHighlightLimitError = useRef(false);

  const { user } = useUser();

  // Highlight-related state
  const [highlightTitle, setHighlightTitle] = useState(user?.highlight_title || 'Our Highlights');
  const [editingTitle, setEditingTitle] = useState(false);
  const [isAddHighlightDrawerOpen, setIsAddHighlightDrawerOpen] = useState(false);
  const [highlightToEdit, setHighlightToEdit] = useState<Record<string, any> | null>(null);
  const [isDeleteHighlightDrawerOpen, setIsDeleteHighlightDrawerOpen] = useState(false);
  const [highlightToDelete, setHighlightToDelete] = useState<Record<string, any> | null>(null);

  const { mutate: patchUserTitle } = UserService.patch({
    mutationParams: {
      onSuccess: () => {
        setSuccessMessage('Title updated successfully');
        setIsSuccessMessageOpen(true);
      },
      onError: (_error) => {
        // Comment out console.error to pass linting, but keep for debugging if needed
        // setSuccessMessage('Failed to update title');
        setIsSuccessMessageOpen(true);
      }
    }
  });

  const { data: links, isLoading } = AuthenticatedLinkService.get({ queryParams: { queryKey: ['link'] } });
  const { data: products } = ProductService.get({ queryParams: { queryKey: ['products'] } });

  // Form for editing highlight title
  const formMethods = useForm({ 
    formName: 'highlight-title-form',
    initialValues: { highlightTitle: 'Our Highlights' },
    persistValues: false,  // Add this to prevent FormProvider warnings
    touchOnChange: false,
    validationSchema: (props: Record<string, any>) => {
      const errors: Record<string, any> = {};
      const { highlightTitle: titleValue } = props;
      if (!titleValue?.trim()) {
        errors.highlightTitle = 'Highlight title is required.';
      }
      return errors;
    },
    submitHandler: async (vals) => {
      const { currentPacket: { highlightTitle: titleValue } } = vals;
      
      // Update backend
      patchUserTitle({ highlight_title: titleValue });
      
      setHighlightTitle(titleValue);
      setEditingTitle(false);
    },
  });

  const { register, onSubmitHandler, formState, setValues } = formMethods;

  const handleOpenDrawer = (link = null) => {
    // Check if user has reached the link limit (3 links)
    const existingLinks = links?.result?.filter((link: any) => link.name !== 'Google Review') || [];
    if (existingLinks.length >= 3 && !link) {
      // Only show the error message once to prevent continuous popups
      if (!hasShownLinkLimitError.current) {
        setSuccessMessage('You can only add up to 3 links.');
        setIsSuccessMessageOpen(true);
        hasShownLinkLimitError.current = true;
        // Reset the flag after a delay to allow showing the message again if needed
        setTimeout(() => {
          hasShownLinkLimitError.current = false;
        }, 5000);
      }
      return;
    }
    
    setEditableLink(link);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditableLink(null);
  };

  const handleCloseSuccessMessage = () => {
    setIsSuccessMessageOpen(false);
  };

  const handleOpenDeleteDrawer = (link: Record<string, any>) => {
    setSelectedLinkForDelete(link);
    setIsDeleteDrawerOpen(true);
    setIsDrawerOpen(false); 
  };

  const handleCloseDeleteDrawer = () => {
    setIsDeleteDrawerOpen(false);
    setSelectedLinkForDelete(null);
  };

  // Highlight-related functions
  const handleAddHighlightClick = (product?: Record<string, any>) => {
    // Check if user has reached the highlight limit (3 highlights)
    const existingProducts = products?.result || [];
    if (existingProducts.length >= 3 && !product) {
      // Only show the error message once to prevent continuous popups
      if (!hasShownHighlightLimitError.current) {
        setSuccessMessage('You can only add up to 3 highlights.');
        setIsSuccessMessageOpen(true);
        hasShownHighlightLimitError.current = true;
        // Reset the flag after a delay to allow showing the message again if needed
        setTimeout(() => {
          hasShownHighlightLimitError.current = false;
        }, 5000);
      }
      return;
    }
    
    setHighlightToEdit(product || null);
    setIsAddHighlightDrawerOpen(true);
  };

  const handleDeleteHighlightClick = (product: Record<string, any>) => {
    setHighlightToDelete(product);
    setIsDeleteHighlightDrawerOpen(true);
  };

  const handleCloseAddHighlightDrawer = () => {
    setIsAddHighlightDrawerOpen(false);
    setHighlightToEdit(null);
  };

  const maxHighlights = 3;
  
  // Create displayHighlights without modifying existing array in place
  const displayHighlights = useMemo(() => {
    const existingProducts = products?.result || [];
    const emptySlots = Math.max(0, maxHighlights - existingProducts.length);
    return [
      ...existingProducts,
      ...Array.from({ length: emptySlots }, (_, i) => ({ isEmpty: true, id: `empty-${i}` }))
    ];
  }, [products?.result]);

  const handleEmptyLinkClick = () => {
    handleOpenDrawer();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleEmptyLinkClick();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <div className="flex h-full w-full flex-col justify-between px-1">
        <div className="small-scrollbar flex h-full flex-col gap-6 overflow-y-auto">
          {/* Links Section */}
          <div className="flex flex-col items-center gap-4">
            {isLoading ? (
              <LinkLoader />
            ) : (
              <>
                {links?.result?.filter((link: any) => link.name !== 'Google Review').map((filteredLink: any) => (
                  // eslint-disable-next-line no-underscore-dangle
                  <Link
                    key={filteredLink.id}
                    link={filteredLink}
                    onEdit={() => handleOpenDrawer(filteredLink)}
                  />
                ))}
                {/* Empty link slots */}
                {Array.from({ length: Math.max(0, 3 - (links?.result?.filter((linkToCheck: any) => linkToCheck.name !== 'Google Review').length || 0)) }).map((_, index) => (
                  <div
                    key={`empty-link-${index}`}
                    className="flex w-full flex-col gap-2 rounded-lg bg-white cursor-pointer hover:bg-gray-50 transition-colors shadow-[0px_1px_2px_0px_#47546733] md:shadow-none md:border md:border-gray-200"
                    onClick={handleEmptyLinkClick}
                    onKeyDown={handleKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label="Add new link"
                  >
                    <div className="flex items-center justify-between p-2 sm:w-full md:p-4">
                      <div className="flex flex-1 items-center gap-3">
                        <div className="w-[2.5rem] flex-shrink-0">
                          <ImageIcon className="h-full w-[2.5rem] object-contain" />
                        </div>
                        <div className="ml-3 flex w-[8rem] flex-1 flex-col gap-1 overflow-hidden sm:flex-1">
                          <span className="font-['Inter'] font-bold text-[13px] leading-[17px] text-[#475467]">Click to add</span>
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center justify-between gap-2">
                        <PlusIconSvg className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Highlights Section */}
          <div className="flex flex-col gap-4">
            {editingTitle ? (
              <form onSubmit={onSubmitHandler} className="flex flex-col gap-2">
                <FormControl
                  controlType="input"
                  placeholder="Our highlights"
                  className="w-full"
                  {...register('highlightTitle')}
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    size="small"
                    className="text-xs"
                    disabled={formState.hasError}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    className="text-xs"
                    onClick={() => setEditingTitle(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-[#475467]">
                  {highlightTitle || 'Our highlights'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditingTitle(true);
                    setValues({ highlightTitle });
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Edit title"
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.7422 9.89453L11.5234 8.67578L9.02344 11.1758L10.2422 12.3945C10.3672 12.5195 10.5234 12.582 10.7109 12.582C10.8984 12.582 11.0547 12.5195 11.1797 12.3945L12.7422 10.832C12.8672 10.707 12.9297 10.5508 12.9297 10.3633C12.9297 10.1758 12.8672 10.0195 12.7422 9.89453ZM0.929688 3.08203L8.30469 10.457L10.8047 7.95703L3.42969 0.582031H0.929688V3.08203Z" fill="#989898"/>
                  </svg>
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-4 px-1 max-[420px]:gap-1 max-[420px]:px-1">
              {displayHighlights.map((product: any, index: number) => (
                product.isEmpty ? (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`empty-${index}`}
                    className="flex flex-col items-center"
                  >
                    <button
                      type="button"
                      onClick={() => handleAddHighlightClick()}
                      className="w-[109.344px] h-[140px] max-[420px]:w-full max-[420px]:h-[140px] max-[420px]:max-w-[125px] max-[420px]:mx-auto flex flex-col items-center justify-center rounded-[8.24px] bg-white border-gray-300 border-[0.51px] backdrop-blur-[10.297px] shadow-[2.57px_2.57px_5.15px_0px_rgba(0,0,0,0.25)] hover:shadow-[2.57px_2.57px_8px_0px_rgba(0,0,0,0.3)] transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#039be5]/60 focus:ring-offset-white md:shadow-none md:border-gray-200"
                    >
                      <div className="flex flex-col items-center justify-center h-full w-full">
                        <PlusIcon className="h-8 w-8 text-[#039BE5]" />
                      </div>
                    </button>
                    <div className="mt-3 text-[12px] text-[#333333] text-center w-full break-words leading-tight">
                      Click to add
                    </div>
                  </div>
                ) : (
                  <Product
                    // eslint-disable-next-line no-underscore-dangle
                    key={product._id || product.id || `product-${index}`}
                    product={product}
                    onClick={handleAddHighlightClick}
                  />
                )
              ))}
            </div>
          </div>
          
        </div>

        <AddLinkDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          link={editableLink}
          onDelete={handleOpenDeleteDrawer}
        />
        <SuccessMessage
          isOpen={isSuccessMessageOpen}
          onClose={handleCloseSuccessMessage}
          message={successMessage}
        />
        <DeleteLinkDrawer
          isOpen={isDeleteDrawerOpen}
          onClose={handleCloseDeleteDrawer}
          link={selectedLinkForDelete}
          onSuccess={() => {
            setSuccessMessage('Link deleted successfully');
            setIsSuccessMessageOpen(true);
            handleCloseDeleteDrawer();
          }}
        />
        <AddProductDrawer
          isOpen={isAddHighlightDrawerOpen}
          onClose={handleCloseAddHighlightDrawer}
          product={highlightToEdit || undefined}
          onDelete={handleDeleteHighlightClick}
        />
        <DeleteProductDrawer
          isOpen={isDeleteHighlightDrawerOpen}
          onClose={() => setIsDeleteHighlightDrawerOpen(false)}
          product={highlightToDelete || null}
          onDeleteSuccess={() => {
            setSuccessMessage('Highlight deleted successfully');
            setIsSuccessMessageOpen(true);
            setIsDeleteHighlightDrawerOpen(false);
            handleCloseAddHighlightDrawer();
          }}
        />
      </div>
    </FormProvider>
  );
}

export default Template1;